<template>
  <div>
    <v-autocomplete
      ref="search"
      v-model="selectedEntry"
      :items="entries"
      item-text="fullName"
      item-value="id"
      label="Search Students or Teachers"
      clearable
      return-object
      :search-input.sync="searchInput"
      @input="updateValue"
      @update:search-input="debouncedFetchEntries"
      @click:clear="clearSearch"
      v-bind="$attrs"
    >
      <template v-slot:item="{ item }">
        <v-list-item-content>
          <v-list-item-title>
            [{{ item.type }}]
            <template
              v-if="
                searchInput &&
                item.fullName &&
                item.fullName.toLowerCase().includes(searchInput.toLowerCase())
              "
            >
              <span
                v-for="(part, index) in splitName(item.fullName)"
                :key="index"
              >
                <strong
                  v-if="
                    searchInput &&
                    part.toLowerCase() === searchInput.toLowerCase()
                  "
                  >{{ part }}</strong
                >
                <span v-else>{{ part }}</span>
              </span>
            </template>
            <template v-else>
              {{ item.fullName }}
            </template>
            {{ item.dob ? `${formatDate(item.dob)}` : "" }}
            {{ item.email ? `(${item.email})` : "" }}
            {{
              !globalCenter || !globalCenter.id
                ? `@ ${getCenterName(item.centerId)}`
                : ""
            }}
          </v-list-item-title>
        </v-list-item-content>
      </template>

      <template v-slot:no-data>
        <v-list-item>
          <v-list-item-content>
            <div class="text-center font-weight-light">No data found.</div>
            <v-divider class="my-2" />
            <div class="d-flex justify-center">
              <v-btn color="primary" text @click="addNewStudent">
                Add New Student
              </v-btn>
              <v-btn color="primary" text @click="addNewTeacher">
                Add New Teacher
              </v-btn>
            </div>

            <div v-if="searchHistory && searchHistory.length > 0">
              <v-divider class="my-2" />
              <div class="text-subtitle-2 font-weight-light">
                Search History
              </div>
              <v-divider />
              <v-chip-group class="d-flex justify-center flex-wrap mt-2">
                <v-chip
                  v-for="(item, index) in searchHistory"
                  :key="index"
                  color="blue-grey lighten-3"
                  text-color="white"
                  @click="updateValue(item)"
                >
                  {{ item.fullName }}
                </v-chip>
              </v-chip-group>
            </div>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>

    <v-dialog v-model="showNewEntryForm" max-width="600px">
      <component
        :is="formType"
        :centers="myCenters"
        @submit="addNewEntry"
        @close="handleDialogClose"
      />
    </v-dialog>
  </div>
</template>


<script>
import _ from "lodash";
import moment from "moment";
import { mapGetters } from "vuex";
import { getLocalStorage, setLocalStorage } from "@/utils/localStorage.js";
import { createTeacher } from "@/services/teacher.js";
import { openStudentScheduleLookup, openTeacherScheduleLookup } from './eventHandler';
const StudentForm = () => import("@/components/student/NewStudentForm.vue");
const TeacherForm = () => import("@/components/teacher/NewTeacherForm.vue");

export default {
  components: {
    StudentForm,
    TeacherForm,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    centerId: {
      type: String,
      default: () => null,
    },
  },
  data() {
    return {
      selectedEntry: this.value,
      entries: [],
      searchInput: "",
      searchHistory: [],
      formType: null,
      showNewEntryForm: false,
    };
  },
  computed: {
    ...mapGetters(["globalCenter", "getCenterName", "myCenters", "currentUser"]),
  },
  created() {
    this.debouncedFetchEntries = _.debounce(this.fetchEntries, 300);

    this.searchHistory = getLocalStorage(this.currentUser.company +"_searchHistory");
    if(!this.searchHistory) {
      this.searchHistory = []
    }
    else{
      this.searchHistory = this.searchHistory.filter(h => h);
    }
  },
  watch: {
    value(newValue) {
      this.selectedEntry = newValue;
    },
  },
  methods: {
    clearSearch() {
      this.searchInput = "";
      this.entries = [];
    },
    async fetchEntries(searchInput) {
      if (!searchInput) {
        // If searchInput is empty, clear the entries and return
        this.entries = [];
        return;
      }

      const [studentResponse, teacherResponse] = await Promise.all([
        this.$axios.get("/student", {
          params: {
            centerId: this.centerId,
            searchTerms: searchInput,
            activeOnly: false,
            classroomIds: null,
            size: 50,
          },
        }),
        this.$axios.get("/teacher", {
          params: {
            "include-all-centers": true,
            searchTerms: searchInput,
            size: 50,
          },
        }),
      ]);

      const students = studentResponse.data.content.map((s) => ({
        ...s,
        type: "Student",
      }));
      const teachers = teacherResponse.data.content.map((t) => ({
        ...t,
        type: "Teacher",
        fullName: `${t.firstName} ${t.lastName}`, // Add this line
      }));

      // Merge and sort by full name
      this.entries = _.sortBy([...students, ...teachers], ["fullName"]);
    },

    updateValue(value) {
      this.addToHistory(value);
      this.$emit("input", value);

      if (value.type == "Student") {
        openStudentScheduleLookup({student: value});
      } else if (value.type == "Teacher") {
        openTeacherScheduleLookup({teacher: value});
      }

    },
    addToHistory(value) {
      const index = this.searchHistory.findIndex(
        (item) => value && item.id === value.id
      );
      if (index === -1) {
        // Only add the item if it's not already in the history
        this.searchHistory.unshift(value); // Add to the start of the history
        this.searchHistory = this.searchHistory.slice(0, 10); // Only keep the last 10

        this.searchHistory = this.searchHistory.filter(h => h); // confirm null doesn't get stored
        setLocalStorage(this.currentUser.company +"_searchHistory", this.searchHistory); // Update the localStorage
      }
    },
    formatDate(dob) {
      if (!dob) {
        return "";
      }

      return `(DoB: ${moment(dob).format("MMM DD YYYY")})`;
    },
    getCenterName(centerId) {
      return this.getCenterName(centerId);
    },
    splitName(name) {
      const searchInputLower = this.searchInput.toLowerCase();
      const nameLower = name.toLowerCase();
      const start = nameLower.indexOf(searchInputLower);

      if (start === -1) return [name];

      if (start === 0) {
        return [
          name.substr(0, this.searchInput.length),
          name.substr(this.searchInput.length),
        ];
      } else {
        return [
          name.substr(0, start),
          name.substr(start, this.searchInput.length),
          name.substr(start + this.searchInput.length),
        ];
      }
    },
    addNewStudent() {
      this.formType = "student-form";
      this.showNewEntryForm = true;
    },
    addNewTeacher() {
      this.formType = "teacher-form";
      this.showNewEntryForm = true;
    },
    async addNewEntry(entry) {
      let newEntry = null;
      // Handle the form submission. Depending on the formType, make the appropriate API call.
      if (this.formType === "student-form") {
        // Handle student form submission
        if (entry) {
          this.students = this.students ? [...this.students, entry] : [entry];
          newEntry = { ...entry, type: "Student" };
        }
      } else if (this.formType === "teacher-form") {
        // Handle teacher form submission
        let savedTeacher = await createTeacher(entry)
          .then( (resp) => {
            return resp.data.data;
          })

        if (savedTeacher) {
          savedTeacher.fullName = `${savedTeacher.firstName} ${savedTeacher.lastName}`; // Add the fullName property
          this.teachers = this.teachers
            ? [...this.teachers, savedTeacher]
            : [savedTeacher];
          newEntry = { ...savedTeacher, type: "Teacher" };
        }
      }

      // If newEntry was created, add it to entries.
      if (newEntry) {
        this.entries = [...this.entries, newEntry];
      }

      // Close the dialog and update the selectedEntry
      this.showNewEntryForm = false;
      this.selectedEntry = newEntry;
    },
    handleDialogClose() {
      this.entries = [];
      this.selectedEntry = null;
      this.showNewEntryForm = false;
    },
  },
};
</script>
